import { forwardRef, useState, useId, useRef, useEffect, useImperativeHandle, useCallback, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.css';

type TrackingValue = string | number | boolean;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hasError?: boolean;
  helpIcon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  hideHelpIconOnStates?: boolean;
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hasError = false,
      helpIcon,
      size = 'md',
      hideHelpIconOnStates = true,
      className,
      id,
      disabled,
      value,
      defaultValue,
      onFocus,
      onBlur,
      onChange,
      trackEvent,
      trackData,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const [isFocused, setIsFocused] = useState(false);
    const internalRef = useRef<HTMLInputElement>(null);
    
    // Combinar ref interno con ref externo
    useImperativeHandle(ref, () => internalRef.current!, []);
    
    // Calcular si hay valor basado en props o estado interno
    const getHasValue = useCallback(() => {
      if (value !== undefined) {
        return String(value || '').trim().length > 0;
      }
      if (internalRef.current) {
        return internalRef.current.value.trim().length > 0;
      }
      return String(defaultValue || '').trim().length > 0;
    }, [value, defaultValue]);

    const [hasValue, setHasValue] = useState(() => getHasValue());
    const hasPlaceholder = Boolean(props.placeholder && props.placeholder.length > 0);
    const showHelpIcon = helpIcon && (!hideHelpIconOnStates || (!hasError && !isFocused));

    // Actualizar hasValue cuando cambie el value prop
    useEffect(() => {
      if (value !== undefined) {
        setHasValue(String(value || '').trim().length > 0);
      }
    }, [value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Actualizar hasValue al perder el foco para componentes no controlados
      if (value === undefined && internalRef.current) {
        setHasValue(internalRef.current.value.trim().length > 0);
      }
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Actualizar hasValue inmediatamente cuando cambie el valor
      setHasValue(e.target.value.trim().length > 0);
      onChange?.(e);
    };

    const isLabelActive = label && (isFocused || hasValue || hasPlaceholder);
    const hasLabel = Boolean(label);

    const containerClasses = [
      styles.container,
      hasError ? styles.error : '',
      isFocused && !hasError ? styles.focused : '',
      disabled ? styles.disabled : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClasses = [
      styles.input,
      styles[size],
      hasLabel ? styles.hasLabel : '',
      isLabelActive ? styles.labelActive : '',
    ]
      .filter(Boolean)
      .join(' ');

    const trackingAttributes: Record<string, string> = {};
    if (trackEvent) {
      trackingAttributes['data-track'] = trackEvent;

      if (trackData) {
        for (const [key, value] of Object.entries(trackData)) {
          if (value == null) continue;
          const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
          trackingAttributes[attrKey] = String(value);
        }
      }
    }

    return (
      <div className={containerClasses}>
        <div className={styles.inputWrapper}>
          {hasLabel && (
            <label
              htmlFor={inputId}
              className={[
                styles.label,
                isLabelActive ? styles.labelActive : '',
                hasError ? styles.labelError : '',
                isFocused && !hasError ? styles.labelFocused : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {label}
            </label>
          )}

          <input
            ref={internalRef}
            id={inputId}
            className={inputClasses}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-invalid={hasError}
            {...trackingAttributes}
            {...props}
          />

          {showHelpIcon && <div className={styles.helpIcon}>{helpIcon}</div>}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';
